import React, { useState, useEffect } from "react";
import { Eye, Send } from "lucide-react";
import {
    getAllCancelRequests,
    getAllCancelledOrders,
    updateCancelOrderStatus,
    completeRefund,
    getAllDeliveryBoys,
    assignReturnDeliveryBoy,
    updateAdminStatusForReturn,
} from "../../services/orderList/order.service";
import { toast } from "react-toastify";

// ❌ REMOVE THIS LINE - Ab zaroorat nahi
// const BASE_URL = "https://api.sonachandiwale.com";

type AdminStatus = "Come to Office" | "Go for Delivery" | "Rejected";
type AdminStatusState = AdminStatus | "";

const adminStatusOptions: AdminStatus[] = ["Come to Office", "Go for Delivery", "Rejected"];

const CancelledOrdersTabs: React.FC = () => {
    const [selectedTab, setSelectedTab] = useState<"requests" | "cancelled">("requests");
    const [orders, setOrders] = useState<any[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [showModal, setShowModal] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState<any>(null);
    const [statusValue, setStatusValue] = useState<string>("");
    const [searchTerm, setSearchTerm] = useState("");

    const [deliveryBoys, setDeliveryBoys] = useState<any[]>([]);
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [assignOrder, setAssignOrder] = useState<any>(null);
    const [selectedDeliveryBoy, setSelectedDeliveryBoy] = useState("");

    const [returnAdminStatus, setReturnAdminStatus] = useState<AdminStatusState>("");
    const [isUpdatingReturn, setIsUpdatingReturn] = useState(false);

    const itemsPerPage = 5;
    const startIndex = (currentPage - 1) * itemsPerPage;

    const fetchDeliveryBoys = async () => {
        try {
            const res = await getAllDeliveryBoys();
            setDeliveryBoys(res?.data?.data || []);
        } catch (error) {
            console.error("Error fetching delivery boys:", error);
            toast.error("Failed to fetch delivery boys");
        }
    };

    useEffect(() => {
        fetchDeliveryBoys();
    }, []);

    const fetchOrders = async () => {
        try {
            let res;
            if (selectedTab === "requests") {
                res = await getAllCancelRequests();
            } else {
                res = await getAllCancelledOrders();
            }
            setOrders(res.data.data || []);
            setCurrentPage(1);
        } catch (error) {
            console.error("Error fetching orders:", error);
        }
    };

    useEffect(() => {
        fetchOrders();
    }, [selectedTab]);

    const openModal = (order: any) => {
        setSelectedOrder(order);
        setStatusValue(
            selectedTab === "requests" ? order.orderCancelStatus : order.refundStatus
        );
        const returnSession = Array.isArray(order?.returnDeliverySession)
            ? order.returnDeliverySession[0]
            : order?.returnDeliverySession;
        setReturnAdminStatus((returnSession?.adminStatus || "") as AdminStatusState);
        setShowModal(true);
    };

    const handleStatusUpdate = async () => {
        try {
            if (!selectedOrder) return;

            if (selectedTab === "requests") {
                if (statusValue === "Approved" || statusValue === "Rejected") {
                    await updateCancelOrderStatus(selectedOrder.orderId, {
                        orderCancelStatus: statusValue as "Approved" | "Rejected",
                    });
                    setOrders((prev) =>
                        prev.map((o) =>
                            o.orderId === selectedOrder.orderId
                                ? { ...o, orderCancelStatus: statusValue }
                                : o
                        )
                    );
                }
            } else {
                await completeRefund(selectedOrder.orderId);
                setOrders((prev) =>
                    prev.map((o) =>
                        o.orderId === selectedOrder.orderId ? { ...o, refundStatus: "Completed" } : o
                    )
                );
            }
            setShowModal(false);
        } catch (error) {
            console.error("Status update error:", error);
            toast.error("Update failed");
        }
    };

    const handleAssignDeliveryBoy = async () => {
        if (!assignOrder?.orderId) {
            toast.warning("Order not selected");
            return;
        }
        if (!selectedDeliveryBoy) {
            toast.warning("Please select delivery boy");
            return;
        }
        if (assignOrder?.returnDeliveryBoyId) {
            toast.warning("Return delivery boy is already assigned to this order!");
            setShowAssignModal(false);
            return;
        }
        try {
            await assignReturnDeliveryBoy(assignOrder.orderId, { deliveryBoyId: selectedDeliveryBoy });
            toast.success("Return delivery boy assigned successfully");
            setShowAssignModal(false);
            setSelectedDeliveryBoy("");
            setAssignOrder(null);
            fetchOrders();
        } catch (error) {
            console.error("Assign error:", error);
            toast.error("Failed to assign return delivery boy");
        }
    };

    const handleReturnAdminStatusUpdate = async () => {
        if (!selectedOrder?.orderId) {
            toast.warning("Order not selected");
            return;
        }
        if (!returnAdminStatus) {
            toast.warning("Please select admin status");
            return;
        }
        const returnSession = Array.isArray(selectedOrder?.returnDeliverySession)
            ? selectedOrder.returnDeliverySession[0]
            : selectedOrder?.returnDeliverySession;
        if (!returnSession) {
            toast.warning("Return delivery session not found for this order");
            return;
        }
        const isAlreadyOutForReturn =
            returnSession.adminStatus === "Go for Delivery" ||
            returnSession.adminStatus === "Rejected";
        if (isAlreadyOutForReturn) {
            toast.warning("Return already marked as Go for Delivery or Rejected");
            return;
        }
        setIsUpdatingReturn(true);
        try {
            await updateAdminStatusForReturn(selectedOrder.orderId, { adminStatus: returnAdminStatus });
            setSelectedOrder((prev: any) => {
                const updatedSession = { ...returnSession, adminStatus: returnAdminStatus };
                return {
                    ...prev,
                    returnDeliverySession: Array.isArray(prev?.returnDeliverySession)
                        ? [updatedSession]
                        : updatedSession,
                };
            });
            setOrders((prev) =>
                prev.map((order) =>
                    order.orderId === selectedOrder.orderId
                        ? {
                              ...order,
                              returnDeliverySession: Array.isArray(order?.returnDeliverySession)
                                  ? [{ ...order.returnDeliverySession[0], adminStatus: returnAdminStatus }]
                                  : { ...order?.returnDeliverySession, adminStatus: returnAdminStatus },
                          }
                        : order
                )
            );
            toast.success(`Return delivery status updated to ${returnAdminStatus}`);
            fetchOrders();
        } catch (error) {
            console.error("Return status update error:", error);
            toast.error("Failed to update return delivery status");
        } finally {
            setIsUpdatingReturn(false);
        }
    };

    const filteredOrders = orders.filter((item) =>
        item.orderId?.toString().toLowerCase().includes(searchTerm.toLowerCase())
    );

    const totalPages = Math.ceil(filteredOrders.length / itemsPerPage);

    const paginatedData = filteredOrders
        .slice(startIndex, startIndex + itemsPerPage)
        .map((item, index) => ({
            ...item,
            serial: startIndex + index + 1,
        }));

    const getReturnSession = (order: any) => {
        if (!order?.returnDeliverySession) return null;
        return Array.isArray(order.returnDeliverySession)
            ? order.returnDeliverySession[0]
            : order.returnDeliverySession;
    };

    const returnSession = getReturnSession(selectedOrder);
    const isReturnPickupImageAvailable = Boolean(returnSession?.buyerPickupImage);
    const isReturnAlreadyOutForDelivery =
        returnSession?.adminStatus === "Go for Delivery" ||
        returnSession?.adminStatus === "Rejected";
    const isReturnUpdateDisabled = !returnAdminStatus || !isReturnPickupImageAvailable || isReturnAlreadyOutForDelivery;

    return (
        <div>
            {/* Tabs and Search */}
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "20px",
                }}
            >
                <div className="tabs" style={{ display: "flex", gap: "10px" }}>
                    <button
                        onClick={() => setSelectedTab("requests")}
                        style={{
                            padding: "6px 12px",
                            borderRadius: "6px",
                            border: selectedTab === "requests" ? "1px solid #FACC15" : "1px solid #333",
                            color: selectedTab === "requests" ? "#FACC15" : "#fff",
                            background: "transparent",
                            fontWeight: selectedTab === "requests" ? "bold" : "normal",
                            cursor: "pointer"
                        }}
                    >
                        Cancel Requests
                    </button>
                    <button
                        onClick={() => setSelectedTab("cancelled")}
                        style={{
                            padding: "6px 12px",
                            borderRadius: "6px",
                            border: selectedTab === "cancelled" ? "1px solid #FACC15" : "1px solid #333",
                            color: selectedTab === "cancelled" ? "#FACC15" : "#fff",
                            background: "transparent",
                            fontWeight: selectedTab === "cancelled" ? "bold" : "normal",
                            cursor: "pointer"
                        }}
                    >
                        Cancelled Orders
                    </button>
                </div>
                <input
                    type="text"
                    placeholder="Search Order ID..."
                    value={searchTerm}
                    onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1);
                    }}
                    className="search-bar"
                    style={{
                        height: "36px",
                        padding: "6px 12px",
                        borderRadius: "6px",
                        border: "1px solid #444",
                        background: "#0f172a",
                        color: "#fff",
                        width: "220px",
                        outline: "none",
                    }}
                />
            </div>

            {/* Table */}
            <table className="employee-table">
                <thead>
                    <tr>
                        <th>S.No</th>
                        <th>Order ID</th>
                        <th>Seller</th>
                        <th>Buyer</th>
                        <th>Purity</th>
                        <th>Quantity</th>
                        <th>Cancel Reasons</th>
                        <th>Making / gm</th>
                        <th>GST %</th>
                        <th>Payment Method</th>
                        <th>Final Amount</th>
                        <th>Discount Amount</th>
                        <th>Final Discount Amount</th>
                        <th>Discount Amount + GST</th>
                        {selectedTab === "cancelled" && (
                            <>
                                <th>Current Status</th>
                                <th>Admin Status</th>
                            </>
                        )}
                        <th>{selectedTab === "requests" ? "Cancel Status" : "Refund Status"}</th>
                        <th>Action</th>
                        {selectedTab === "cancelled" && <th>Assign</th>}
                    </tr>
                </thead>
                <tbody>
                    {paginatedData.map((item) => {
                        const returnSession = Array.isArray(item?.returnDeliverySession)
                            ? item.returnDeliverySession[0]
                            : item?.returnDeliverySession;
                        const currentStatus = returnSession?.currentStatus || "-";
                        const adminStatus = returnSession?.adminStatus || "-";

                        return (
                            <tr key={item.orderId}>
                                <td>{item.serial}</td>
                                <td>{item.orderId}</td>
                                <td>{item.matchSellerName || "-"}</td>
                                <td>{item.BuyerName || "-"}</td>
                                <td>{item.purity}</td>
                                <td>{item.quantity}</td>
                                <td>{item.orderCancelReasons}</td>
                                <td>₹ {item.makingChargesPerGram}</td>
                                <td>{item.gstPercent}%</td>
                                <td>{item.PaymentMethod}</td>
                                <td>₹ {item.buyertotalAmmoumtGst}</td>
                                <td>₹ {item.discountAmount || 0}</td>
                                <td>₹ {item.discountAfterAmount || 0}</td>
                                <td>₹ {item.discountAfterAmountWithGst || 0}</td>
                                {selectedTab === "cancelled" && (
                                    <>
                                        <td>{currentStatus}</td>
                                        <td>
                                            <span
                                                style={{
                                                    color:
                                                        adminStatus === "Go for Delivery"
                                                            ? "#4ade80"
                                                            : adminStatus === "Rejected"
                                                            ? "#f87171"
                                                            : adminStatus === "Come to Office"
                                                            ? "#facc15"
                                                            : "#94a3b8",
                                                    fontWeight: "bold",
                                                }}
                                            >
                                                {adminStatus}
                                            </span>
                                        </td>
                                    </>
                                )}
                                <td>
                                    <span
                                        style={{
                                            color:
                                                selectedTab === "requests"
                                                    ? item.orderCancelStatus === "Approved"
                                                        ? "green"
                                                        : item.orderCancelStatus === "Rejected"
                                                        ? "red"
                                                        : "orange"
                                                    : item.refundStatus === "Completed"
                                                    ? "green"
                                                    : item.refundStatus === "Initiated"
                                                    ? "orange"
                                                    : "gray",
                                            fontWeight: "bold",
                                        }}
                                    >
                                        {selectedTab === "requests"
                                            ? item.orderCancelStatus
                                            : item.refundStatus || "-"}
                                    </span>
                                </td>
                                <td>
                                    <button
                                        onClick={() => openModal(item)}
                                        style={{ background: "none", border: "none", cursor: "pointer" }}
                                    >
                                        <Eye size={18} color="white" />
                                    </button>
                                </td>
                                {selectedTab === "cancelled" && (
                                    <td>
                                        <button
                                            onClick={() => {
                                                if (item.returnDeliveryBoyId) {
                                                    toast.warning("Return delivery boy already assigned!");
                                                    return;
                                                }
                                                setAssignOrder(item);
                                                setSelectedDeliveryBoy("");
                                                setShowAssignModal(true);
                                            }}
                                            style={{
                                                color: "#fff",
                                                border: "none",
                                                padding: "6px 12px",
                                                borderRadius: "4px",
                                                display: "inline-flex",
                                                alignItems: "center",
                                                gap: "5px",
                                                background: item.returnDeliveryBoyId ? "#aaa" : "#4CAF50",
                                                cursor: item.returnDeliveryBoyId ? "not-allowed" : "pointer",
                                            }}
                                            disabled={Boolean(item.returnDeliveryBoyId)}
                                            title={item.returnDeliveryBoyId ? "Return Boy Already Assigned" : "Assign Return Delivery Boy"}
                                        >
                                            <Send size={16} /> Assign
                                        </button>
                                    </td>
                                )}
                            </tr>
                        );
                    })}
                </tbody>
            </table>

            {/* Pagination */}
            <div className="pagination">
                <button
                    onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                    disabled={currentPage === 1}
                >
                    Previous
                </button>
                <span>
                    Page {currentPage} of {totalPages || 1}
                </span>
                <button
                    onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                    disabled={currentPage === totalPages}
                >
                    Next
                </button>
            </div>

            {/* Order Details Modal */}
            {showModal && selectedOrder && (
                <div className="modal-overlay">
                    <div className="modal-box" style={{ width: "900px", maxHeight: "90vh", overflowY: "auto", backgroundColor: "#0f172a", borderRadius: "20px", padding: "24px", color: "#e2e8f0" }}>
                        <h3 style={{ color: "#facc15" }}>Order Details - {selectedOrder.orderId}</h3>

                        <table className="employee-table" style={{ background: "#1e293b", borderRadius: "12px", marginTop: "16px", width: "100%" }}>
                            <tbody>
                                {[
                                    { label: "Order ID", value: selectedOrder.orderId },
                                    { label: "Seller", value: selectedOrder.matchSellerName },
                                    { label: "Buyer", value: selectedOrder.BuyerName },
                                    { label: "Category ID", value: selectedOrder.categoryId },
                                    { label: "Article ID", value: selectedOrder.articleId },
                                    { label: "UOM ID", value: selectedOrder.uomId },
                                    { label: "Metal Type ID", value: selectedOrder.metalTypeId },
                                    { label: "Purity", value: selectedOrder.purity },
                                    { label: "Karat", value: selectedOrder.karat },
                                    { label: "Carat", value: selectedOrder.carat },
                                    { label: "Cent Value", value: selectedOrder.centValue },
                                    { label: "Weight", value: selectedOrder.weight },
                                    { label: "Quantity", value: selectedOrder.quantity },
                                    { label: "Wastage %", value: selectedOrder.wastagePercent },
                                    { label: "Making / gm", value: selectedOrder.makingChargesPerGram },
                                    { label: "Commission %", value: selectedOrder.commissionPercent },
                                    { label: "GST %", value: selectedOrder.gstPercent },
                                    { label: "Payment Method", value: selectedOrder.PaymentMethod },
                                    { label: "Total Amount (Buyer)", value: selectedOrder.buyertotalAmount },
                                    { label: "Total Amount + GST (Buyer)", value: selectedOrder.buyertotalAmmoumtGst },
                                    { label: "Total Amount (Seller)", value: selectedOrder.sellertotalAmount },
                                ].map((field) => (
                                    <tr key={field.label}>
                                        <td style={{ fontWeight: "bold", color: "#94a3b8" }}>{field.label}</td>
                                        <td>{field.value || "-"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {/* Return Delivery Control Section */}
                        {selectedTab === "cancelled" && returnSession && (
                            <div style={{ marginTop: "32px", borderTop: "1px solid #334155", paddingTop: "24px" }}>
                                <h4 style={{ display: "flex", alignItems: "center", gap: "8px", color: "#facc15" }}>
                                    🔄 Return Delivery Control
                                </h4>

                                <div style={{ background: "#1e293b", borderRadius: "16px", padding: "16px", marginBottom: "20px" }}>
                                    <div style={{ display: "flex", gap: "24px", flexWrap: "wrap", marginBottom: "16px" }}>
                                        <div>
                                            <span style={{ fontWeight: "500", color: "#cbd5e1" }}>📌 Order Status: </span>
                                            <span style={{ color: "#facc15", fontWeight: "bold" }}>{returnSession.currentStatus || "-"}</span>
                                        </div>
                                        <div>
                                            <span style={{ fontWeight: "500", color: "#cbd5e1" }}>🏷️ Admin Status: </span>
                                            <span style={{ color: returnSession.adminStatus === "Go for Delivery" ? "#4ade80" : returnSession.adminStatus === "Rejected" ? "#f87171" : "#facc15", fontWeight: "bold" }}>
                                                {returnSession.adminStatus || "Pending"}
                                            </span>
                                        </div>
                                    </div>

                                    {/* ✅ FIXED: Removed BASE_URL - Directly use backend URLs */}
                                    <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: "20px" }}>
                                        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                                            <span style={{ fontWeight: "500", color: "#cbd5e1" }}>📸 Return Pickup Image:</span>
                                            {returnSession.buyerPickupImage ? (
                                                <img
                                                    src={returnSession.buyerPickupImage}
                                                    alt="return pickup"
                                                    style={{ width: "70px", height: "70px", objectFit: "cover", borderRadius: "12px", cursor: "pointer", border: "1px solid #475569" }}
                                                    onClick={() => window.open(returnSession.buyerPickupImage, "_blank")}
                                                />
                                            ) : (
                                                <span style={{ color: "#94a3b8" }}>No Image</span>
                                            )}
                                        </div>
                                        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                                            <span style={{ fontWeight: "500", color: "#cbd5e1" }}>🚚 Return Delivery Image:</span>
                                            {returnSession.sellerDeliveryImage ? (
                                                <img
                                                    src={returnSession.sellerDeliveryImage}
                                                    alt="return delivery"
                                                    style={{ width: "70px", height: "70px", objectFit: "cover", borderRadius: "12px", cursor: "pointer", border: "1px solid #475569" }}
                                                    onClick={() => window.open(returnSession.sellerDeliveryImage, "_blank")}
                                                />
                                            ) : (
                                                <span style={{ color: "#94a3b8" }}>No Image</span>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Admin Status Dropdown */}
                                <div style={{ background: "#1e293b", borderRadius: "16px", padding: "18px", border: "1px solid #334155" }}>
                                    <div style={{ display: "flex", gap: "12px", alignItems: "center", flexWrap: "wrap" }}>
                                        <select
                                            disabled={isReturnAlreadyOutForDelivery}
                                            value={returnAdminStatus}
                                            onChange={(e) => setReturnAdminStatus(e.target.value as AdminStatusState)}
                                            style={{
                                                flex: 1,
                                                padding: "10px 14px",
                                                borderRadius: "12px",
                                                background: isReturnAlreadyOutForDelivery ? "#334155" : "#0f172a",
                                                color: "#f1f5f9",
                                                border: "1px solid #475569",
                                                cursor: isReturnAlreadyOutForDelivery ? "not-allowed" : "pointer",
                                                fontWeight: "500",
                                            }}
                                        >
                                            <option value="">Select Admin Status</option>
                                            {adminStatusOptions.map((status) => (
                                                <option key={status} value={status}>{status}</option>
                                            ))}
                                        </select>
                                        <button
                                            disabled={isReturnUpdateDisabled || isUpdatingReturn}
                                            style={{
                                                background: isReturnUpdateDisabled ? "#475569" : "#f97316",
                                                color: "#fff",
                                                padding: "10px 18px",
                                                border: "none",
                                                borderRadius: "40px",
                                                cursor: isReturnUpdateDisabled ? "not-allowed" : "pointer",
                                                fontWeight: "600",
                                            }}
                                            onClick={handleReturnAdminStatusUpdate}
                                        >
                                            {isUpdatingReturn ? "Updating..." : "Update Return Status 🚚"}
                                        </button>
                                    </div>
                                    {!isReturnPickupImageAvailable && (
                                        <div style={{ fontSize: "12px", color: "#facc15", marginTop: "10px" }}>
                                            ⚠️ Return pickup image required before updating delivery status
                                        </div>
                                    )}
                                    {isReturnAlreadyOutForDelivery && (
                                        <div style={{ fontSize: "12px", color: "#f97316", marginTop: "10px" }}>
                                            ℹ️ Return already marked as {returnSession.adminStatus} – cannot change
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Status update dropdown */}
                        <div style={{ marginTop: "20px" }}>
                            <h4>{selectedTab === "requests" ? "Update Cancel Status" : "Update Refund Status"}</h4>
                            <div style={{ position: "relative", display: "inline-block" }}>
                                <select
                                    value={statusValue}
                                    onChange={(e) => setStatusValue(e.target.value)}
                                    style={{
                                        appearance: "none",
                                        WebkitAppearance: "none",
                                        MozAppearance: "none",
                                        padding: "6px 30px 6px 10px",
                                        borderRadius: "4px",
                                        border: "1px solid #ccc",
                                        fontSize: "14px",
                                        cursor: "pointer",
                                    }}
                                >
                                    {selectedTab === "requests"
                                        ? ["Requested", "Approved", "Rejected"].map((s) => (
                                            <option key={s} value={s}>{s}</option>
                                        ))
                                        : ["Pending", "Initiated", "Completed"].map((s) => (
                                            <option key={s} value={s}>{s}</option>
                                        ))}
                                </select>
                                <span
                                    style={{
                                        position: "absolute",
                                        right: "10px",
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                        pointerEvents: "none",
                                        fontSize: "12px",
                                        color: "#555",
                                    }}
                                >
                                    ▼
                                </span>
                            </div>
                        </div>

                        <div style={{ marginTop: "15px", display: "flex", gap: "10px" }}>
                            <button
                                onClick={handleStatusUpdate}
                                style={{
                                    backgroundColor: "#FACC15",
                                    border: "none",
                                    color: "#000",
                                    fontWeight: "bold",
                                    padding: "6px 12px",
                                    borderRadius: "4px",
                                    cursor: "pointer",
                                }}
                            >
                                Update
                            </button>
                            <button
                                onClick={() => setShowModal(false)}
                                style={{
                                    padding: "6px 12px",
                                    borderRadius: "4px",
                                    border: "1px solid #fffefe",
                                    background: "transparent",
                                    cursor: "pointer",
                                }}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Assign Delivery Boy Modal */}
            {showAssignModal && assignOrder && (
                <div className="modal-overlay" style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: "rgba(0,0,0,0.5)",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    zIndex: 1000,
                }}>
                    <div style={{
                        backgroundColor: "white",
                        borderRadius: "8px",
                        padding: "20px",
                        width: "400px",
                    }}>
                        <h3>🚚 Assign Return Delivery Boy</h3>
                        <select
                            value={selectedDeliveryBoy}
                            onChange={(e) => setSelectedDeliveryBoy(e.target.value)}
                            style={{
                                width: "100%",
                                padding: "8px",
                                marginBottom: "15px",
                                borderRadius: "4px",
                                border: "1px solid #ccc",
                            }}
                        >
                            <option value="">Select Delivery Boy</option>
                            {deliveryBoys.map((boy) => (
                                <option key={boy.id} value={boy.id}>
                                    {boy.firstName} {boy.lastName}
                                </option>
                            ))}
                        </select>
                        <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
                            <button
                                onClick={handleAssignDeliveryBoy}
                                style={{
                                    background: "#2196F3",
                                    color: "#fff",
                                    padding: "8px 12px",
                                    border: "none",
                                    borderRadius: "6px",
                                    cursor: "pointer",
                                }}
                            >
                                Assign
                            </button>
                            <button
                                onClick={() => setShowAssignModal(false)}
                                style={{
                                    background: "#777",
                                    color: "#fff",
                                    padding: "8px 12px",
                                    border: "none",
                                    borderRadius: "6px",
                                    cursor: "pointer",
                                }}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CancelledOrdersTabs;